import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { formatDateKey, formatDateRange, getMonday } from './adminCalendar/calendarUtils';
import DayColumn from './adminCalendar/DayColumn';
import CreateSlotModal from './adminCalendar/CreateSlotModal';
import AddClientModal from './adminCalendar/AddClientModal';
import DuplicateWeekModal from './adminCalendar/DuplicateWeekModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// ADMIN CALENDAR - SLOT & BOOKING MANAGEMENT
// UI Language: English (Admin Portal)
// Orchestration only — presentational pieces live in ./adminCalendar/
// ============================================
const AdminCalendar = ({ timeSlots, bookings, clients, workouts = [], onRefresh, onCreateSlot, t }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getMonday(new Date()));
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState(new Set());

  // Modals
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedSlotForClient, setSelectedSlotForClient] = useState(null);
  const [initialDate, setInitialDate] = useState('');

  // Navigate to client planner
  const handleClientClick = (booking) => {
    const clientId = booking.client_id;
    const bookingDate = booking.date;
    // Navigate to client detail page with planner tab and date in URL
    window.location.href = `/admin/clients/${clientId}?tab=planner&date=${bookingDate}`;
  };

  // Week dates (Mon-Sat only, no Sunday)
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 6; i++) { // 6 days: Mon-Sat
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentWeekStart]);

  // Week stats
  const weekSlots = useMemo(() => {
    const startStr = formatDateKey(weekDates[0]);
    const endStr = formatDateKey(weekDates[5]); // Saturday
    return timeSlots.filter(s => s.date >= startStr && s.date <= endStr);
  }, [timeSlots, weekDates]);

  const weekBookings = useMemo(() => {
    const startStr = formatDateKey(weekDates[0]);
    const endStr = formatDateKey(weekDates[5]); // Saturday
    return bookings.filter(b => b.date >= startStr && b.date <= endStr && b.status !== 'cancelled');
  }, [bookings, weekDates]);

  const weekStats = useMemo(() => {
    const totalCapacity = weekSlots.reduce((sum, s) => sum + (s.max_clients || 6), 0);

    return {
      slots: weekSlots.length,
      bookings: weekBookings.length,
      capacity: totalCapacity
    };
  }, [weekSlots, weekBookings]);

  // Recover "ghost" bookings for DISPLAY: a booking whose time_slot_id points to
  // a slot that no longer exists (deleted / recreated / duplicated) renders
  // nowhere, yet the backend still blocks re-booking because its duplicate-check
  // keys on client+date+start_time. Re-attach each such orphan to the slot that
  // currently occupies the same date+start_time so the trainer can see & manage
  // it. booking.id is preserved, so cancel/remove still target the real record.
  const displayBookings = useMemo(() => {
    const slotIds = new Set(timeSlots.map(s => s.id));
    // Stable pick: first slot id (by id) for each date|start_time key.
    const slotByKey = {};
    for (const s of [...timeSlots].sort((a, b) => String(a.id).localeCompare(String(b.id)))) {
      const key = `${s.date}|${s.start_time}`;
      if (!(key in slotByKey)) slotByKey[key] = s.id;
    }
    return bookings.map((b) => {
      if (b.status === 'cancelled') return b;
      if (b.time_slot_id && slotIds.has(b.time_slot_id)) return b;
      const target = slotByKey[`${b.date}|${b.start_time}`];
      return target ? { ...b, time_slot_id: target } : b;
    });
  }, [bookings, timeSlots]);

  // Navigation
  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    setCurrentWeekStart(getMonday(new Date()));
  };

  // Slot actions
  const handleCreateSlot = async (formData, slotId) => {
    try {
      const token = localStorage.getItem('token');
      if (slotId) {
        // Edit existing slot
        await axios.put(`${API}/timeslots/${slotId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        onRefresh();
      } else {
        // Create new slot using the provided function
        await onCreateSlot(formData);
      }
      setShowSlotModal(false);
      setEditingSlot(null);
    } catch (error) {
      console.error('Error saving slot:', error);
      alert('Error saving slot: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleDeleteSlot = async (slot) => {
    try {
      const token = localStorage.getItem('token');
      // Fetch delete preview to show impact
      const previewRes = await axios.get(`${API}/timeslots/${slot.id}/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const preview = previewRes.data;

      let confirmMsg = `Delete slot ${slot.start_time} - ${slot.end_time}?`;
      if (preview.bookings_count > 0) {
        const clientNames = preview.bookings.map(b => `  - ${b.client_name} (${b.status})`).join('\n');
        confirmMsg = `⚠️ This slot has ${preview.bookings_count} active booking(s):\n${clientNames}`;
        if (preview.workouts_count > 0) {
          confirmMsg += `\n\n${preview.workouts_count} workout(s) will also be deleted.`;
        }
        confirmMsg += `\n\nAre you sure you want to delete this slot?`;
      }

      if (!window.confirm(confirmMsg)) return;

      await axios.delete(`${API}/timeslots/${slot.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting slot:', error);
      alert('Error deleting slot: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSlots.size === 0) return;
    if (!window.confirm(`Delete ${selectedSlots.size} selected slots?`)) return;

    try {
      const token = localStorage.getItem('token');
      for (const slotId of selectedSlots) {
        await axios.delete(`${API}/timeslots/${slotId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSelectedSlots(new Set());
      setBulkMode(false);
      onRefresh();
    } catch (error) {
      console.error('Error deleting slots:', error);
      alert('Error deleting some slots');
    }
  };

  // Clear bookings from selected slots (keeps slots, removes clients)
  const handleBulkClearBookings = async () => {
    if (selectedSlots.size === 0) return;

    // Find all bookings for selected slots
    const bookingsToCancel = bookings.filter(
      b => selectedSlots.has(b.time_slot_id) && b.status !== 'cancelled'
    );

    if (bookingsToCancel.length === 0) {
      alert('No bookings to clear from selected slots');
      return;
    }

    if (!window.confirm(`Clear ${bookingsToCancel.length} bookings from ${selectedSlots.size} slots?`)) return;

    try {
      const token = localStorage.getItem('token');
      for (const booking of bookingsToCancel) {
        await axios.post(`${API}/bookings/${booking.id}/cancel`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSelectedSlots(new Set());
      setBulkMode(false);
      onRefresh();
    } catch (error) {
      console.error('Error clearing bookings:', error);
      alert('Error clearing some bookings');
    }
  };

  // Client assignment - using trainer-book endpoint for admins
  const handleAssignClient = async (slot, client) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/trainer-book?slot_id=${slot.id}&client_id=${client.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowClientModal(false);
      setSelectedSlotForClient(null);
      onRefresh();
    } catch (error) {
      console.error('Error assigning client:', error);
      alert('Error assigning client: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Remove client from slot (cancel booking) - using POST for trainer/admin endpoint
  const handleRemoveClient = async (booking) => {
    if (!window.confirm(`Remove client from this slot?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${booking.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      console.error('Error removing client:', error);
      alert('Error removing client: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Duplicate week - now receives array of target week dates and copy_bookings flag
  const handleDuplicateWeek = async (targetWeekStarts, copyBookings = false) => {
    const sourceWeekStart = formatDateKey(currentWeekStart);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/bookings/duplicate-week`, {
        source_week_start: sourceWeekStart,
        target_week_starts: targetWeekStarts,
        copy_bookings: copyBookings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { results, source_slots_count, source_bookings_count } = response.data;

      // Build summary message
      let totalSlotsCreated = 0;
      let totalBookingsCreated = 0;
      Object.values(results).forEach(result => {
        totalSlotsCreated += result.slots_created || 0;
        totalBookingsCreated += result.bookings_created || 0;
      });

      let message = `✅ Week duplicated to ${targetWeekStarts.length} week(s)\n\n`;
      message += `📅 Slots created: ${totalSlotsCreated}`;
      if (copyBookings) {
        message += `\n👥 Bookings created: ${totalBookingsCreated}`;
      }

      onRefresh();
      alert(message);
    } catch (error) {
      console.error('Error duplicating week:', error);
      alert('Error duplicating week: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  // Get counts for current week
  const currentWeekSlotsCount = useMemo(() => {
    return weekSlots.length;
  }, [weekSlots]);

  const currentWeekBookingsCount = useMemo(() => {
    return weekBookings.filter(b => b.status !== 'cancelled').length;
  }, [weekBookings]);

  // Toggle slot selection - supports single slot or array of slots
  const toggleSlotSelect = (slotIdOrIds, forceSelect = null) => {
    setSelectedSlots(prev => {
      const newSelected = new Set(prev);

      // Handle array of slot IDs (for Select All)
      const slotIds = Array.isArray(slotIdOrIds) ? slotIdOrIds : [slotIdOrIds];

      slotIds.forEach(slotId => {
        if (forceSelect === true) {
          newSelected.add(slotId);
        } else if (forceSelect === false) {
          newSelected.delete(slotId);
        } else {
          // Toggle
          if (newSelected.has(slotId)) {
            newSelected.delete(slotId);
          } else {
            newSelected.add(slotId);
          }
        }
      });

      return newSelected;
    });
  };

  return (
    <div className="space-y-4">
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Calendar & Schedule</h1>
          <p className="text-slate-400 text-sm">
            {weekStats.bookings} bookings • {weekStats.slots} slots • {weekStats.capacity} total capacity
          </p>
        </div>
        <button onClick={goToToday} className="btn-secondary text-sm">
          Today
        </button>
      </div>

      {/* ============================================ */}
      {/* TOOLBAR */}
      {/* ============================================ */}
      <div className="flex flex-wrap gap-2 p-3 bg-slate-800/30 rounded-lg">
        <button
          onClick={() => { setEditingSlot(null); setInitialDate(''); setShowSlotModal(true); }}
          className="btn-primary text-sm"
        >
          + Create Slot
        </button>
        <button
          onClick={() => setShowDuplicateModal(true)}
          className="btn-secondary text-sm"
        >
          Duplicate Week
        </button>
        <button
          onClick={() => {
            setBulkMode(!bulkMode);
            if (bulkMode) setSelectedSlots(new Set());
          }}
          className={`btn-secondary text-sm ${bulkMode ? 'bg-purple-500/30 text-purple-200' : ''}`}
        >
          {bulkMode ? '✕ Cancel' : '☑️ Multi Select'}
        </button>

        {bulkMode && selectedSlots.size > 0 && (
          <>
            <button onClick={handleBulkClearBookings} className="btn-secondary text-sm ml-auto border-amber-500/50 text-amber-300 hover:bg-amber-500/20">
              Clear bookings ({selectedSlots.size})
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 text-sm rounded-lg bg-red-500/20 text-red-300 border border-red-500/50 hover:bg-red-500/30 transition"
            >
              🗑️ Delete slots ({selectedSlots.size})
            </button>
          </>
        )}
      </div>

      {/* ============================================ */}
      {/* WEEK NAVIGATION */}
      {/* ============================================ */}
      <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
        <button onClick={() => navigateWeek(-1)} className="btn-secondary text-sm">
          ← Previous Week
        </button>
        <div className="text-center">
          <span className="text-white font-semibold">
            {formatDateRange(weekDates[0], weekDates[5])}
          </span>
        </div>
        <button onClick={() => navigateWeek(1)} className="btn-secondary text-sm">
          Next Week →
        </button>
      </div>

      {/* ============================================ */}
      {/* DAY SLOT BOARD (Mon-Sat, 6 columns) */}
      {/* ============================================ */}
      <div className="grid grid-cols-6 gap-2">
        {weekDates.map((date, idx) => (
          <DayColumn
            key={idx}
            date={date}
            slots={timeSlots}
            bookings={displayBookings}
            clients={clients}
            workouts={workouts}
            onAddClient={(slot) => { setSelectedSlotForClient(slot); setShowClientModal(true); }}
            onRemoveClient={handleRemoveClient}
            onEditSlot={(slot) => { setEditingSlot(slot); setShowSlotModal(true); }}
            onDeleteSlot={handleDeleteSlot}
            onAddSlot={(date) => { setInitialDate(formatDateKey(date)); setShowSlotModal(true); }}
            bulkMode={bulkMode}
            selectedSlots={selectedSlots}
            onToggleSelect={toggleSlotSelect}
            onClientClick={handleClientClick}
          />
        ))}
      </div>

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}
      <CreateSlotModal
        isOpen={showSlotModal}
        onClose={() => { setShowSlotModal(false); setEditingSlot(null); }}
        onSubmit={handleCreateSlot}
        initialDate={initialDate}
        editingSlot={editingSlot}
      />

      <AddClientModal
        isOpen={showClientModal}
        onClose={() => { setShowClientModal(false); setSelectedSlotForClient(null); }}
        slot={selectedSlotForClient}
        clients={clients}
        bookings={bookings}
        onAssign={handleAssignClient}
      />

      <DuplicateWeekModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        currentWeekStart={currentWeekStart}
        onDuplicate={handleDuplicateWeek}
        slotsCount={currentWeekSlotsCount}
        bookingsCount={currentWeekBookingsCount}
      />
    </div>
  );
};

export default AdminCalendar;
